import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Handle gallery view toggle (regular vs imperfecties)
    const viewButtons = this.querySelectorAll('[data-gallery-view]');
    if (viewButtons.length > 0) {
      viewButtons.forEach((button) => {
        button.addEventListener('click', this.#handleViewToggle, { signal });
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles the gallery view toggle button click (regular vs imperfecties).
   * Switches between showing regular gallery images or imperfecties images only.
   */
  #handleViewToggle = (event) => {
    const clickedButton = event.currentTarget;
    const viewType = clickedButton.getAttribute('data-gallery-view');
    const isImperfectiesView = viewType === 'imperfecties';

    // Update button states
    const allViewButtons = this.querySelectorAll('[data-gallery-view]');
    allViewButtons.forEach((button) => {
      const isActive = button === clickedButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    // Get media counts from data attributes
    const regularCount = parseInt(this.dataset.regularMediaCount || '0', 10);
    const imperfectiesCount = parseInt(this.dataset.imperfectiesMediaCount || '0', 10);

    // Find all regular media elements (slides, grid items, zoom dialog items)
    const allSlides = this.querySelectorAll('slideshow-slide');
    const allGridItems = this.querySelectorAll('.media-gallery__grid > li');
    const allZoomItems = this.querySelectorAll('.dialog-zoomed-gallery > li');
    const allThumbnails = this.querySelectorAll('.slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail');

    // Show/hide regular media
    allSlides.forEach((slide, index) => {
      const isImperfecties = slide.hasAttribute('data-imperfecties-image');
      if (isImperfectiesView) {
        // Imperfecties view: hide regular, show imperfecties
        slide.hidden = !isImperfecties;
        if (isImperfecties) {
          slide.setAttribute('reveal', '');
          slide.setAttribute('aria-hidden', 'false');
        } else {
          slide.removeAttribute('reveal');
          slide.setAttribute('aria-hidden', 'true');
        }
      } else {
        // Regular view: show regular, hide imperfecties
        slide.hidden = isImperfecties;
        if (!isImperfecties) {
          slide.removeAttribute('reveal');
          slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
        } else {
          slide.removeAttribute('reveal');
          slide.setAttribute('aria-hidden', 'true');
        }
      }
    });

    // Handle grid items
    allGridItems.forEach((item) => {
      const isImperfecties = item.hasAttribute('data-imperfecties-image');
      item.hidden = isImperfectiesView ? !isImperfecties : isImperfecties;
    });

    // Handle zoom dialog items
    allZoomItems.forEach((item) => {
      const isImperfecties = item.hasAttribute('data-imperfecties-image');
      item.hidden = isImperfectiesView ? !isImperfecties : isImperfecties;
    });

    // Handle thumbnails
    allThumbnails.forEach((thumbnail, index) => {
      const isImperfecties = thumbnail.hasAttribute('data-imperfecties-image');
      const img = thumbnail.querySelector('img');
      const isImperfectiesByAlt = img && img.alt === 'Imperfecties';
      const isImperfectiesThumbnail = isImperfecties || isImperfectiesByAlt;
      
      thumbnail.hidden = isImperfectiesView ? !isImperfectiesThumbnail : isImperfectiesThumbnail;
    });

    // Update counter display to show correct count for current view
    const slideshowControls = this.querySelectorAll('slideshow-controls');
    const visibleCount = isImperfectiesView ? imperfectiesCount : regularCount;
    
    slideshowControls.forEach((control) => {
      const counter = control.querySelector('.slideshow-controls__counter');
      if (counter) {
        // The counter structure is: <span ref="current">1</span><span class="slash">/</span>10
        // We need to find the text node or element after the slash
        const slash = counter.querySelector('.slash');
        if (slash && slash.nextSibling) {
          // Update the text node or element after the slash
          const nextNode = slash.nextSibling;
          if (nextNode.nodeType === Node.TEXT_NODE) {
            nextNode.textContent = String(visibleCount);
          } else if (nextNode.nodeType === Node.ELEMENT_NODE) {
            nextNode.textContent = String(visibleCount);
          }
        } else {
          // Fallback: update the entire counter text
          const currentSpan = counter.querySelector('span[ref="current"]');
          if (currentSpan) {
            const currentValue = currentSpan.textContent.trim();
            counter.textContent = `${currentValue}/${visibleCount}`;
          }
        }
      }
    });

    // Update slideshow
    const slideshow = this.refs.slideshow;
    if (slideshow) {
      // Reset to first slide of the active view
      // In imperfecties view, the first imperfecties slide is at index regularCount
      // But after filtering, it will be at index 0 in the visible slides
      const targetIndex = isImperfectiesView ? regularCount : 0;
      
      // Find the actual slide element to select
      const allSlidesArray = Array.from(allSlides);
      const targetSlide = allSlidesArray[targetIndex];
      
      if (targetSlide && !targetSlide.hidden) {
        // Use the slide's slide-id if available, otherwise use index
        const slideId = targetSlide.getAttribute('slide-id');
        if (slideId) {
          slideshow.select({ id: slideId }, undefined, { animate: false });
        } else {
          slideshow.select(targetIndex, undefined, { animate: false });
        }
      } else {
        // Fallback: select first visible slide
        const firstVisibleSlide = allSlidesArray.find((slide) => !slide.hidden);
        if (firstVisibleSlide) {
          const slideId = firstVisibleSlide.getAttribute('slide-id');
          if (slideId) {
            slideshow.select({ id: slideId }, undefined, { animate: false });
          } else {
            const firstVisibleIndex = allSlidesArray.indexOf(firstVisibleSlide);
            slideshow.select(firstVisibleIndex, undefined, { animate: false });
          }
        }
      }
      
      // Update the slideshow's current property to reflect the new view
      // This will update the counter display
      queueMicrotask(() => {
        if (slideshow.current !== undefined) {
          // Force update of counter by triggering a slide change event
          const currentSlide = slideshow.slides?.[slideshow.current];
          if (currentSlide) {
            slideshow.current = slideshow.current;
          }
        }
      });
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
